import React from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Msg #991 — Admin: browse all users. Toggle All / Online-only. Click a name
// to view their character profile (parent supplies onView).
export default function UsersListModal({ open, onClose, requester, onView }) {
  const [mode, setMode] = React.useState('all');   // 'all' | 'online'
  const [rows, setRows] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [q, setQ] = React.useState('');

  const load = React.useCallback(async () => {
    setBusy(true); setErr('');
    try {
      if (mode === 'online') {
        const r = await axios.get(`${API}/presence/list`);
        setRows((r.data?.users || []).map((u) => ({
          name: u.name, color: u.color, room: u.room,
          is_admin: !!u.is_admin, is_moderator: !!u.is_moderator,
          rank: u.rank, kills: u.kills, online: true,
        })));
      } else {
        const r = await axios.get(`${API}/admin/users/list`, { params: { requester } });
        setRows(r.data?.users || []);
      }
    } catch (e) {
      const d = e && e.response && e.response.data && e.response.data.detail;
      setErr(d || 'Failed to load users.');
    } finally { setBusy(false); }
  }, [mode, requester]);

  React.useEffect(() => { if (open) { setQ(''); load(); } }, [open, load]);
  if (!open) return null;

  const filtered = rows.filter((u) =>
    !q.trim() || String(u.name).toLowerCase().includes(q.trim().toLowerCase()),
  );

  return (
    <div
      data-testid="users-list-modal"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(20,40,20,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(96vw, 620px)', maxHeight: '86vh',
          display: 'flex', flexDirection: 'column',
          background: '#d6e8c5', border: '2px solid #2d6b1e',
          fontFamily: 'Arial, Helvetica, sans-serif', color: '#1c3f1c',
        }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '10px 14px', background: '#1c3a1c', color: '#ecfcd9',
          borderBottom: '1px solid #2d6b1e',
        }}>
          <div style={{ fontWeight: 'bold', letterSpacing: 2, textTransform: 'uppercase', fontSize: 14 }}>
            👥 Users
          </div>
          <button
            onClick={onClose}
            data-testid="users-list-close"
            style={{ background: '#c0392b', color: '#fff',
                     border: '1px solid #7a1f14', padding: '2px 8px',
                     fontWeight: 'bold', cursor: 'pointer', borderRadius: 4 }}
          >✕</button>
        </div>
        <div style={{ display: 'flex', gap: 6, padding: '10px 14px', alignItems: 'center', flexWrap: 'wrap',
                      borderBottom: '1px dashed #5b8c47' }}>
          <button
            data-testid="users-list-mode-all"
            onClick={() => setMode('all')}
            style={{
              background: mode === 'all' ? '#2d6b1e' : '#f5faee',
              color: mode === 'all' ? '#ecfcd9' : '#1c3a1c',
              border: '1px solid #2d6b1e', padding: '3px 10px',
              fontSize: 11, fontWeight: 'bold', letterSpacing: 1,
              cursor: 'pointer',
            }}
          >ALL</button>
          <button
            data-testid="users-list-mode-online"
            onClick={() => setMode('online')}
            style={{
              background: mode === 'online' ? '#2d6b1e' : '#f5faee',
              color: mode === 'online' ? '#ecfcd9' : '#1c3a1c',
              border: '1px solid #2d6b1e', padding: '3px 10px',
              fontSize: 11, fontWeight: 'bold', letterSpacing: 1,
              cursor: 'pointer',
            }}
          >ONLINE ONLY</button>
          <input
            data-testid="users-list-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="filter by name…"
            style={{ flex: 1, padding: '3px 8px', border: '1px solid #5b8c47', fontSize: 12 }}
          />
          <span style={{ fontSize: 11, opacity: 0.8 }}>
            {filtered.length} {filtered.length === 1 ? 'user' : 'users'}{busy ? ' · loading…' : ''}
          </span>
        </div>
        {err && <div data-testid="users-list-error" style={{ padding: '4px 14px', color: '#7a1f1f', fontSize: 12 }}>{err}</div>}
        <div data-testid="users-list-rows" style={{ flex: 1, overflowY: 'auto', background: '#f5faee' }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', opacity: 0.7, fontSize: 12 }}>No users found.</div>
          ) : filtered.map((u) => (
            <div
              key={(u.name_lc || u.name || '').toLowerCase()}
              data-testid={`users-list-row-${(u.name_lc || u.name || '').toLowerCase()}`}
              style={{
                padding: '6px 14px',
                borderBottom: '1px dashed #b4d497',
                display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 12,
                cursor: 'pointer',
              }}
              onClick={() => {
                if (onView) onView({ name: u.name, color: u.color, isNPC: false });
              }}
              title={`View ${u.name}'s profile`}
            >
              <span style={{ width: 10, height: 10, borderRadius: '50%',
                background: u.online ? '#21a85a' : '#8a8a8a', border: '1px solid #333' }} />
              <b style={{ color: u.color || '#1c3a1c', textDecoration: 'underline dotted' }}>{u.name}</b>
              {u.is_admin && <span style={{ fontSize: 10, color: '#a08a30' }}>🌟 admin</span>}
              {u.is_moderator && !u.is_admin && <span style={{ fontSize: 10, color: '#5a7a8a' }}>♦ mod</span>}
              {u.room && u.online && <span style={{ marginLeft: 'auto', fontSize: 10, opacity: 0.7 }}>in {u.room}</span>}
              {u.rank && !u.online && <span style={{ marginLeft: 'auto', fontSize: 10, opacity: 0.7 }}>{u.rank}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
